import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { readStoredAdminFlag } from "../authAdmin";
import { isViewerAdministrator } from "./userMatrixStorage";
import { fetchAdminProjectPhaseHistory } from "./projectPhaseHistoryApi";
import { formatCommercialDays } from "./commercialReportData";
import { useBoardTheme } from "./boardThemeStore";
import "./reportingPage.css";

ModuleRegistry.registerModules([AllCommunityModule]);

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function whenFormatter(params) {
  return formatWhen(params.value);
}

function daysFormatter(params) {
  return formatCommercialDays(params.value);
}

const AVERAGE_COLUMNS = [
  { field: "phase", headerName: "Phase", width: 130, pinned: "left" },
  { field: "completed_stays", headerName: "Completed Stays", width: 140, type: "numericColumn" },
  {
    field: "avg_days",
    headerName: "Avg Days",
    width: 110,
    valueFormatter: daysFormatter,
    type: "numericColumn",
  },
];

const HISTORY_COLUMNS = [
  { field: "work_order", headerName: "Work Order", width: 120, pinned: "left" },
  { field: "customer", headerName: "Customer", width: 160 },
  { field: "project_name", headerName: "Project", width: 180 },
  { field: "phase", headerName: "Phase", width: 120 },
  {
    field: "entered_at",
    headerName: "Entered",
    width: 170,
    valueFormatter: whenFormatter,
  },
  {
    field: "exited_at",
    headerName: "Exited",
    width: 170,
    valueFormatter: (params) => (params.data?.is_current ? "Current" : formatWhen(params.value)),
  },
  {
    field: "duration_days",
    headerName: "Days",
    width: 90,
    valueFormatter: daysFormatter,
    type: "numericColumn",
  },
  { field: "source", headerName: "Source", width: 150 },
  { field: "recorded_by", headerName: "Recorded By", width: 180 },
];

export function ProjectPhaseHistoryGate() {
  if (!readStoredAdminFlag() && !isViewerAdministrator()) {
    return <Navigate to="/" replace />;
  }
  return <ProjectPhaseHistoryPage />;
}

export default function ProjectPhaseHistoryPage() {
  const { agGridTheme } = useBoardTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [averages, setAverages] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminProjectPhaseHistory();
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setAverages(Array.isArray(data?.averages_by_phase) ? data.averages_by_phase : []);
    } catch (err) {
      setRows([]);
      setAverages([]);
      setError(err?.response?.data?.error || err.message || "Could not load phase history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        id: String(row.id),
        ...row,
        is_current: row.is_current ?? row.exited_at == null,
      })),
    [rows]
  );

  const averageRows = useMemo(
    () => averages.map((row) => ({ id: row.phase, ...row })),
    [averages]
  );

  return (
    <div className="reporting-shell">
      <div className="reporting-page">
        <header className="reporting-page-header">
          <h1 className="reporting-page-title">Project Phase History</h1>
          <p className="reporting-page-lead">
            Administrator view of how long each commercial project spent in every workflow phase.
            Intervals are recorded when a phase is completed on the project landing page. Older
            projects will be empty until they advance again.
          </p>
        </header>

        <p className="reporting-note">
          Change log behavior is unchanged. This table is a dedicated phase timeline used for
          reporting averages. New commercial projects receive an open interval for their starting
          phase; completing a phase closes that interval and opens the next.
        </p>

        {loading ? <p className="reporting-status">Loading phase history…</p> : null}
        {error ? <p className="reporting-status reporting-status--error">{error}</p> : null}

        {!loading && !error ? (
          <>
            <h2 className="reporting-section-title">Average days per phase (completed stays)</h2>
            <div className="reporting-grid-wrap">
              <AgGridReact
                theme={agGridTheme}
                rowData={averageRows}
                columnDefs={AVERAGE_COLUMNS}
                getRowId={(params) => params.data.id}
                defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 80 }}
                domLayout="autoHeight"
                suppressCellFocus
              />
            </div>

            <h2 className="reporting-section-title">All phase intervals</h2>
            {gridRows.length === 0 ? (
              <p className="reporting-placeholder">
                No phase history yet. Complete a phase on a commercial project to start recording
                intervals.
              </p>
            ) : (
              <div className="reporting-grid-wrap">
                <AgGridReact
                  theme={agGridTheme}
                  rowData={gridRows}
                  columnDefs={HISTORY_COLUMNS}
                  getRowId={(params) => params.data.id}
                  defaultColDef={{ sortable: true, filter: true, resizable: true, minWidth: 80 }}
                  domLayout="autoHeight"
                  suppressCellFocus
                />
              </div>
            )}
          </>
        ) : null}

        <p className="reporting-status">
          <Link to="/reporting">Reporting</Link>
        </p>
      </div>
    </div>
  );
}
